import React, { useState, useRef } from 'react';
import SEO from '../../components/SEO';
import PageBanner from '../../components/PageBanner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import { Loader2, Briefcase, Users, Zap } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ReCAPTCHA from "react-google-recaptcha";
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';
import { useCountries } from '../../hooks/useCountries';

const Careers = () => {
    const { language } = useLanguage();
    const t = translations[language].careers;
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const recaptchaRef = useRef(null);
    const fileInputRef = useRef(null);
    const { countries } = useCountries();

    const schema = yup.object().shape({
        title: yup.string().required("Title is required"),
        firstName: yup.string().required("First Name is required"),
        lastName: yup.string().required("Last Name is required"),
        phone: yup.string().matches(/^[0-9]+$/, "Phone must be digits").min(8, "Phone must be at least 8 digits").required("Phone number is required"),
        nationality: yup.string().required("Nationality is required"),
        jobTitle: yup.string().required("Position of Interest is required"),
        email: yup.string().email("Invalid email").required("Email is required"),
        message: yup.string().required("Message is required"),
        cv: yup.mixed().required("CV is required").test("fileSize", "File too large", (value) => {
            if (!value || value.length === 0) return false;
            // 5MB limit
            return value[0].size <= 5000000;
        })
    });

    const { register, handleSubmit, control, formState: { errors }, reset, setValue, watch, trigger, setError } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            title: '',
            firstName: '',
            lastName: '',
            phone: '',
            nationality: '',
            jobTitle: '',
            email: '',
            message: '',
            countryCode: '+968'
        }
    });

    const selectedNationality = watch("nationality");

    React.useEffect(() => {
        if (selectedNationality) {
            const country = countries.find(c => String(c.id) === String(selectedNationality));
            setValue("countryCode", (country && country.phone_prefix) || "+968");
        } else if (countries.length > 0) {
            const oman = countries.find(c => c.name.toLowerCase() === 'oman');
            if (oman) {
                setValue("nationality", String(oman.id));
                setValue("countryCode", oman.phone_prefix || "+968");
            }
        }
    }, [selectedNationality, setValue, countries]);

    const cvFile = watch("cv");

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    const handleFileChange = (e) => {
        const file = e.target.files;
        if (file) {
            setValue("cv", file);
            trigger("cv");
        }
    };

    const onSubmit = async (data) => {
        const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
        if (recaptchaSiteKey && !captchaToken) {
            toast({
                title: "Validation Error",
                description: "Please complete the ReCAPTCHA verification.",
                variant: "destructive",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('title', data.title);
            formData.append('firstName', data.firstName);
            formData.append('lastName', data.lastName);
            formData.append('phone', `${data.countryCode}${data.phone}`);
            formData.append('nationality', data.nationality);
            formData.append('jobTitle', data.jobTitle);
            formData.append('email', data.email);
            formData.append('message', data.message);
            formData.append('cv', data.cv[0]);
            formData.append('recaptcha_token', captchaToken);

            const response = await axios.post('https://admin.baicoman.com/api/job-application-leads', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.status === 200 || response.status === 201) {
                toast({
                    title: "Success",
                    description: "Your application has been submitted successfully.",
                    className: "bg-green-600 text-white border-none"
                });
                reset();
                if (recaptchaSiteKey && recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }
                setCaptchaToken(null);
            }
        } catch (error) {
            console.error(error);
            if (error.response && error.response.data && error.response.data.errors) {
                const serverErrors = error.response.data.errors;
                Object.keys(serverErrors).forEach((field) => {
                    setError(field, {
                        type: "server",
                        message: serverErrors[field][0],
                    });
                });
                toast({
                    title: "Validation Error",
                    description: "Please check the form for errors.",
                    variant: "destructive",
                });
            } else {
                toast({
                    title: "Error",
                    description: "Something went wrong. Please try again.",
                    variant: "destructive",
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pt-20 lg:pt-[120px]">
            <SEO
                title="Careers at BAIC Oman | Join Our Team"
                description="Explore career opportunities at BAIC Oman. Submit your application to join our team of automotive innovators."
                canonicalUrl="https://baicoman.com/connect/careers"
            />
            <h1 className="sr-only">Careers at BAIC Oman</h1>

            <PageBanner
                image="/assets/images/image-careers.png"
                imageAlt="Careers at BAIC"
                contentClassName='items-end'
            >
                <h2 className="text-2xl md:text-6xl font-bold text-white uppercase tracking-tight">{t.hero.title}</h2>
                <p className="text-xl text-gray-200 mt-4 max-w-2xl">{t.hero.subtitle}</p>
            </PageBanner>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

                    {/* Left Column: Application Form (8 columns) */}
                    <div className="lg:col-span-8">
                        <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 uppercase">{t.generalApp.title}</h2>
                            <p className="text-gray-500 mb-8">
                                {t.generalApp.desc}
                            </p>

                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                {/* Title & Name */}
                                {/* Title, First Name & Last Name */}
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                                    <div className="md:col-span-1 space-y-2">
                                        <Label className="text-gray-700 font-semibold">{t.form.title}</Label>
                                        <Controller
                                            control={control}
                                            name="title"
                                            render={({ field }) => (
                                                <Select onValueChange={field.onChange} value={field.value}>
                                                    <SelectTrigger className="w-full bg-gray-50 border-0 h-12 rounded-xl">
                                                        <SelectValue placeholder={t.form.placeholders.select} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="Mr">{t.form.options.mr}</SelectItem>
                                                        <SelectItem value="Ms">{t.form.options.ms}</SelectItem>
                                                        <SelectItem value="Mrs">{t.form.options.mrs}</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />
                                        {errors.title && <p className="text-red-500 text-xs">{errors.title.message}</p>}
                                    </div>
                                    <div className="md:col-span-2 space-y-2">
                                        <Label className="text-gray-700 font-semibold">{t.form.firstName}</Label>
                                        <Input {...register("firstName")} className="bg-gray-50 border-0 h-12 rounded-xl" placeholder={t.form.placeholders.firstName} />
                                        {errors.firstName && <p className="text-red-500 text-xs">{errors.firstName.message}</p>}
                                    </div>
                                    <div className="md:col-span-2 space-y-2">
                                        <Label className="text-gray-700 font-semibold">{t.form.lastName}</Label>
                                        <Input {...register("lastName")} className="bg-gray-50 border-0 h-12 rounded-xl" placeholder={t.form.placeholders.lastName} />
                                        {errors.lastName && <p className="text-red-500 text-xs">{errors.lastName.message}</p>}
                                    </div>
                                </div>

                                {/* Contact Info */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <Label className="text-gray-700 font-semibold">{t.form.email}</Label>
                                        <Input {...register("email")} type="email" className="bg-gray-50 border-0 h-12 rounded-xl" placeholder={t.form.placeholders.email} />
                                        {errors.email && <p className="text-red-500 text-xs">{errors.email.message}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-gray-700 font-semibold">{t.form.phone}</Label>
                                        <div className="flex gap-2">
                                            <div className="bg-gray-50 flex items-center px-4 rounded-xl h-12 text-gray-500 font-medium min-w-[80px] justify-center" dir="ltr">
                                                {watch("countryCode") || "+968"}
                                            </div>
                                            <Input {...register("phone")} className="bg-gray-50 border-0 h-12 rounded-xl flex-1" placeholder={t.form.placeholders.phone} />
                                        </div>
                                        {errors.phone && <p className="text-red-500 text-xs">{errors.phone.message}</p>}
                                    </div>
                                </div>

                                {/* Nationality & Position */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <Label className="text-gray-700 font-semibold">{t.form.nationality}</Label>
                                        <Controller
                                            control={control}
                                            name="nationality"
                                            render={({ field }) => (
                                                <Select onValueChange={field.onChange} value={field.value}>
                                                    <SelectTrigger className="w-full bg-gray-50 border-0 h-12 rounded-xl">
                                                        <SelectValue placeholder={t.form.placeholders.nationality} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {countries.map((country, index) => (
                                                            <SelectItem key={index} value={String(country.id)}>{country.name}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />
                                        {errors.nationality && <p className="text-red-500 text-xs">{errors.nationality.message}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-gray-700 font-semibold">{t.form.position}</Label>
                                        <Input {...register("jobTitle")} className="bg-gray-50 border-0 h-12 rounded-xl" placeholder={t.form.placeholders.position} />
                                        {errors.jobTitle && <p className="text-red-500 text-xs">{errors.jobTitle.message}</p>}
                                    </div>
                                </div>

                                {/* CV Upload */}
                                <div className="space-y-2">
                                    <Label className="text-gray-700 font-semibold">{t.form.cv}</Label>
                                    <div className="bg-gray-50 rounded-xl p-4 flex flex-col md:flex-row items-center gap-4 border-2 border-dashed border-gray-200 hover:border-red-200 transition-colors">
                                        <Button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            variant="outline"
                                            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                                        >
                                            {t.form.chooseFile}
                                        </Button>
                                        <span className="text-gray-500 text-sm truncate max-w-[200px]">
                                            {cvFile && cvFile.length > 0 ? cvFile[0].name : t.form.noFile}
                                        </span>
                                        <input
                                            type="file"
                                            accept=".pdf,.doc,.docx"
                                            className="hidden"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                        />
                                    </div>
                                    {errors.cv && <p className="text-red-500 text-xs">{errors.cv.message}</p>}
                                </div>

                                {/* Message */}
                                <div className="space-y-2">
                                    <Label className="text-gray-700 font-semibold">{t.form.message}</Label>
                                    <Textarea {...register("message")} className="bg-gray-50 border-0 rounded-xl min-h-[150px] p-4 resize-none" placeholder={t.form.placeholders.message} />
                                    {errors.message && <p className="text-red-500 text-xs">{errors.message.message}</p>}
                                </div>

                                {/* Submit & Recaptcha */}
                                <div className="pt-4 flex flex-col items-start gap-6">
                                    {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                                        <div className="space-y-2">
                                            <ReCAPTCHA
                                                ref={recaptchaRef}
                                                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                                onChange={handleCaptchaChange}
                                            />
                                            {errors.recaptcha_token && <p className="text-red-500 text-xs">{errors.recaptcha_token.message}</p>}
                                        </div>
                                    )}
                                    <Button disabled={isSubmitting} type="submit" className="bg-red-600 hover:bg-red-700 text-white rounded-full px-12 py-6 text-sm uppercase tracking-widest font-bold w-full md:w-auto shadow-lg hover:shadow-xl transition-all">
                                        {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : t.form.submit}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Right Column: Information (4 columns) */}
                    <div className="lg:col-span-4 space-y-8 sticky top-[88px] h-fit">
                        {/* Why Join Us Card */}
                        <div className="bg-black text-white p-8 rounded-3xl shadow-xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-red-600 rounded-full blur-[60px] opacity-20"></div>
                            <h3 className="text-2xl font-bold mb-6 uppercase tracking-wide">{t.whyJoin.title}</h3>

                            <div className="space-y-8">
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                                        <Briefcase className="w-6 h-6 text-red-500" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-lg mb-2">{t.whyJoin.growth.title}</h4>
                                        <p className="text-gray-400 text-sm leading-relaxed">
                                            {t.whyJoin.growth.desc}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                                        <Zap className="w-6 h-6 text-red-500" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-lg mb-2">{t.whyJoin.innovation.title}</h4>
                                        <p className="text-gray-400 text-sm leading-relaxed">
                                            {t.whyJoin.innovation.desc}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                                        <Users className="w-6 h-6 text-red-500" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-lg mb-2">{t.whyJoin.culture.title}</h4>
                                        <p className="text-gray-400 text-sm leading-relaxed">
                                            {t.whyJoin.culture.desc}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Careers;
